import { useEffect, useState } from "react";
import { Plus, Trash2, MapPin, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg, fmtDate } from "@/lib/api";
import { ContactButtons } from "@/components/common";
import { mapsLink } from "@/components/LocateMe";

export const LEAD_STATUSES = ["new", "contacted", "meeting", "quoted", "won", "lost"];

async function openFile(id) {
  const r = await api.get(`/files/${id}`, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
}

export function BuildLeadCard({ l, builders, reload }) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const b = builders.find((x) => x.key === l.builder);
  const upd = async (body) => {
    try { await api.post(`/build-leads/${l.id}/status`, body); toast.success(t("bl.updated")); setNote(""); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const feature = async (featured) => {
    try { await api.post(`/owner/home-designs/${l.id}/feature`, { featured }); toast.success(t(featured ? "dz.g_on" : "dz.g_off")); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const facts = [["b.land", l.land && t(`b.land_${l.land}`)], ["b.lot", l.lot_size], ["b.size", l.home_size], ["cl.beds", l.bedrooms], ["cl.baths", l.bathrooms], ["b.budget", l.budget], ["b.timeline", l.timeline], ["b.style", l.style]].filter(([, v]) => v);
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`build-lead-${l.ref}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-slate-500">{l.ref} · {fmtDate(l.created_at)}</p>
          <p className="text-lg font-semibold">{l.name} <span className="text-sm font-normal text-slate-500">· {l.city}</span></p>
          <p className="text-sm text-slate-600">{l.phone}{l.email && ` · ${l.email}`} · {t("b.builder")}: <b>{b ? b.name : t("bl.any")}</b></p>
        </div>
        <select className="h-9 rounded-md border px-2 text-sm bg-white" value={l.status} onChange={(e) => upd({ status: e.target.value })} data-testid={`build-lead-status-${l.ref}`}>
          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{t(`bl.st_${s}`)}</option>)}
        </select>
      </div>
      <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">{facts.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{t(k)}</dt><dd>{v}</dd></div>)}</dl>
      {l.design && (
        <div className="mt-3 flex items-center gap-3" data-testid={`build-lead-design-${l.ref}`}>
          <span className="text-xs font-semibold rounded-full bg-blue-600 text-white px-2 py-0.5">{t("dz.render")}</span>
          {l.design.render_status === "done" ? <a href={`${process.env.REACT_APP_BACKEND_URL}/api/home-designs/${l.id}/image`} target="_blank" rel="noreferrer"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/home-designs/${l.id}/image`} alt="AI home design render" className="h-24 w-36 object-cover rounded-xl border" /></a>
            : <span className="text-xs text-slate-500">{l.design.render_status}</span>}
          {l.design.render_status === "done" && (
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid={`build-lead-feature-${l.ref}`}>
              <input type="checkbox" checked={!!l.design.featured} onChange={(e) => feature(e.target.checked)} className="h-4 w-4 accent-blue-600" data-testid={`build-lead-feature-toggle-${l.ref}`} />
              {t("dz.g_show")}
            </label>
          )}
        </div>
      )}
      {l.description && <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{l.description}</p>}
      <div className="mt-3 flex flex-wrap gap-3 items-center">
        <ContactButtons phone={l.phone} text={t("o.wa_text", { name: l.name, n: l.ref })} />
        {l.geo && <a href={mapsLink(l.geo)} target="_blank" rel="noreferrer" className="text-sm text-blue-700 inline-flex items-center gap-1" data-testid={`build-lead-map-${l.ref}`}><MapPin className="h-4 w-4" />{t("loc.map")}</a>}
        {(l.files || []).map((f) => <button key={f.id} onClick={() => openFile(f.id)} className="text-sm text-blue-700 inline-flex items-center gap-1 hover:underline"><Paperclip className="h-3.5 w-3.5" />{f.original_filename}</button>)}
      </div>
      <div className="mt-3 text-xs text-slate-500 space-y-0.5">{(l.history || []).slice(-4).map((h, i) => <p key={i}>{fmtDate(h.at)} · {t(`bl.st_${h.status}`)}{h.note && ` — ${h.note}`}{h.by && ` (${h.by})`}</p>)}</div>
      <div className="mt-3 flex gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("bl.note")} className="h-9" data-testid={`build-lead-note-${l.ref}`} />
        <Button size="sm" variant="outline" disabled={!note.trim()} onClick={() => upd({ note })} data-testid={`build-lead-note-btn-${l.ref}`}>{t("o.add")}</Button>
      </div>
    </div>
  );
}

const FIELDS = [["name", "o.name"], ["company", "bl.company"], ["specialty", "bl.specialty"], ["specialty_es", "bl.specialty"], ["phone", "f.phone"], ["email", "o.login_email"]];

function BuildersEditor({ builders, setBuilders }) {
  const { t } = useI18n();
  const upd = (i, k, v) => setBuilders(builders.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const save = async () => {
    try { const r = await api.put("/owner/builders", { builders }); setBuilders(r.data); toast.success(t("bl.saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4" data-testid="builders-editor">
      <div><p className="font-semibold">{t("bl.builders_t")}</p><p className="text-xs text-slate-500">{t("bl.email_hint")}</p></div>
      {builders.map((b, i) => (
        <div key={i} className="rounded-xl border border-slate-200 p-3 grid md:grid-cols-3 gap-2" data-testid={`builder-row-${i}`}>
          {FIELDS.map(([k, l]) => <Input key={k} value={b[k] || ""} placeholder={`${t(l)}${k.endsWith("_es") ? " (ES)" : ""}`} onChange={(e) => upd(i, k, e.target.value)} data-testid={`builder-${k}-${i}`} />)}
          <Textarea rows={2} className="md:col-span-3" value={b.bio || ""} placeholder={t("bl.bio")} onChange={(e) => upd(i, "bio", e.target.value)} data-testid={`builder-bio-${i}`} />
          <Textarea rows={2} className="md:col-span-3" value={b.bio_es || ""} placeholder={`${t("bl.bio")} (ES)`} onChange={(e) => upd(i, "bio_es", e.target.value)} data-testid={`builder-bio_es-${i}`} />
          <button type="button" onClick={() => setBuilders(builders.filter((_, j) => j !== i))} className="justify-self-start text-sm text-red-600 inline-flex items-center gap-1" data-testid={`builder-remove-${i}`}><Trash2 className="h-4 w-4" />{t("o.remove")}</button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setBuilders([...builders, { name: "", company: "", specialty: "", phone: "", email: "" }])} data-testid="builder-add-btn"><Plus className="h-4 w-4 me-1" />{t("bl.add")}</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="builders-save-btn">{t("bl.save")}</Button>
      </div>
    </div>
  );
}

export default function OwnerBuilds() {
  const { t } = useI18n();
  const [leads, setLeads] = useState(null);
  const [builders, setBuilders] = useState([]);
  const load = () => api.get("/owner/build-leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); api.get("/owner/builders").then((r) => setBuilders(r.data)); }, []);
  return (
    <div className="space-y-6" data-testid="owner-builds">
      {leads && leads.length === 0 && <p className="text-sm text-slate-500" data-testid="build-leads-empty">{t("bl.none")}</p>}
      {(leads || []).map((l) => <BuildLeadCard key={l.id} l={l} builders={builders} reload={load} />)}
      <BuildersEditor builders={builders} setBuilders={setBuilders} />
    </div>
  );
}
